import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { apiFetch, assignIfStripe } from "@/lib/api";
import { clearIntendedPlan, getIntendedPlan } from "@/lib/intended-plan";
import { CheckoutResponseSchema } from "@/lib/schemas";

// /workspaces/me auto-creates on miss (D11 fallback), so the post-sign-up
// workspace is always ready by the time this route mounts. The poll +
// timeout dance from earlier drafts is gone. The remaining job is: if the
// user came from `/sign-up?plan=pro` (which stashed an intended-plan in
// sessionStorage), fire /billing/checkout and let Stripe take over;
// otherwise drop them into /sites.
export const Route = createFileRoute("/onboarding")({
  component: OnboardingPage,
});

function OnboardingPage() {
  const navigate = useNavigate();
  const billingURL = `${window.location.origin}/settings/billing`;

  const checkout = useMutation({
    mutationFn: ({ plan }: { plan: "pro" | "scale" }) =>
      apiFetch("/billing/checkout", CheckoutResponseSchema, {
        method: "POST",
        body: JSON.stringify({
          plan,
          interval: "month",
          success_url: `${billingURL}?checkout=success`,
          cancel_url: `${billingURL}?checkout=canceled`,
        }),
      }),
    onSuccess: ({ url }) => {
      if (!assignIfStripe(url)) {
        navigate({ to: "/sites" });
      }
    },
    onError: () => navigate({ to: "/sites" }),
  });

  // sessionStorage is cleared before mutate fires, so a StrictMode double
  // mount in development reads null on the second pass and falls through
  // to /sites — not a duplicate checkout.
  const started = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount; the started ref guards against re-entry
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const plan = getIntendedPlan();
    if (plan) {
      clearIntendedPlan();
      checkout.mutate({ plan });
    } else {
      navigate({ to: "/sites" });
    }
  }, [navigate]);

  return (
    <div className="min-h-dvh bg-background grid place-items-center px-6">
      <div className="text-center space-y-4">
        <Loader2 className="mx-auto size-7 animate-spin text-primary" />
        <h1 className="font-display text-xl">Setting up your workspace…</h1>
        <p className="text-sm text-muted-foreground">One moment.</p>
      </div>
    </div>
  );
}
